import { memo, useCallback, useMemo, useState } from 'react'
import { orderBy, times } from 'lodash'

import { useDispatch, useSelector } from '@tabeeb/store'

import {
  AddOutlined,
  EditOutlined,
  GroupAddOutlined,
  GroupRemoveOutlined,
  KeyboardArrowLeft,
} from '@mui/icons-material'

import { Alert, List, ListItemButton, ListItemIcon } from '@mui/material'

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  SearchTextField,
  Subheader2,
  Tooltip,
  UserInfo,
  Stack,
} from '@collaborate-health/uikit'
import { UserRole } from '@tabeeb/enums'
import { useDebouncedValue } from '@tabeeb/shared/utils/hooks'
import { sessionTitleFormatter, trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { getUsersListForCurrentUser } from '@tabeeb/modules/../users/selectors'
import { PluginUser } from '@tabeeb/modules/../users/types'

import { useTrainingMaterials } from '../../hooks'

import NoTrainingMaterialsFoundPlaceholder from '../NoTrainingMaterialsFoundPlaceholder'
import TrainingMaterialListItem, { TrainingMaterialListItemSkeleton } from '../TrainingMaterialListItem'

import { View } from './TrainingMaterialsAttachDialog.styles'

export interface TrainingMaterialsAttachDialogProps {
  open: boolean
  onClose: () => void
  contentId?: number
  onUpdated: () => void
}

const TrainingMaterialsAttachDialog: React.FC<TrainingMaterialsAttachDialogProps> = ({
  open,
  onClose,
  contentId,
  onUpdated,
}) => {
  const dispatch = useDispatch()

  const onError = useCallback(() => {
    dispatch(
      onAddErrorNotification({ message: trainingMaterialTitleFormatter.format('Failed to load training materials') })
    )
    onClose()
  }, [dispatch, onClose])

  const [isUsersConfigurationOpen, setIsUsersConfigurationOpen] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<PluginUser[]>([])
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebouncedValue(searchText, 500)

  const currentUserId = useSelector<number>(getCurrentUserId)
  const users = useSelector<PluginUser[]>(getUsersListForCurrentUser)
  const visibleUsers = useMemo(
    () =>
      orderBy(
        users.filter((u) => !u.isDeleted && u.role !== UserRole.AIUser && u.role !== UserRole.Reviewer),
        (user) => user.id !== currentUserId
      ),
    [currentUserId, users]
  )

  const { loading, loaded, trainingMaterials, isAttachInProgress, onAttach } = useTrainingMaterials({
    enabled: open,
    contentId,
    attach: true,
    search: debouncedSearchText,
    onError,
    onAttached: onUpdated,
  })

  const onSelectUser = useCallback((user) => {
    setSelectedUsers((prevUsers) => {
      if (prevUsers.includes(user)) {
        return prevUsers.filter((u) => u !== user)
      }

      return [...prevUsers, user]
    })
  }, [])

  const onSelectAll = useCallback(() => {
    setSelectedUsers(visibleUsers)
  }, [visibleUsers])

  const onClearAll = useCallback(() => {
    setSelectedUsers([])
  }, [])

  const onReset = useCallback(() => {
    setIsUsersConfigurationOpen(false)
    setSelectedUsers(visibleUsers)
  }, [visibleUsers])

  const noUsersSelected = selectedUsers.length === 0
  const allUsersSelected = visibleUsers.length === selectedUsers.length

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      onClose={onClose}
      TransitionProps={{ onEnter: onReset, onExited: onReset }}
    >
      <DialogTitle display='flex' alignItems='center'>
        {trainingMaterialTitleFormatter.format('Attach training material')}
      </DialogTitle>
      <DialogContent sx={{ padding: 0, overflow: 'hidden', position: 'relative', height: 450 }}>
        <View data-active-view={!isUsersConfigurationOpen} data-main-view>
          <Alert
            sx={{ marginInline: 2, mb: 1, alignItems: 'center' }}
            color={noUsersSelected ? 'warning' : 'info'}
            severity={noUsersSelected ? 'warning' : 'info'}
            action={
              <Button
                disabled={loading || isAttachInProgress}
                startIcon={<EditOutlined />}
                size='small'
                color='primary'
                variant='outlined'
                onClick={() => setIsUsersConfigurationOpen(true)}
              >
                Edit
              </Button>
            }
          >
            {selectedUsers.length === 0
              ? 'No users selected'
              : trainingMaterialTitleFormatter.format(
                  `Training material will be assigned to ${
                    allUsersSelected
                      ? sessionTitleFormatter.format('all session users')
                      : `${selectedUsers.length} selected user(s)`
                  }`
                )}
          </Alert>

          <Stack paddingInline={2} paddingBottom={1}>
            <SearchTextField
              fullWidth
              size='small'
              placeholder='Search...'
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Stack>

          <List sx={{ overflow: 'auto', paddingInline: 2, paddingLeft: 3.5 }}>
            {!loaded &&
              trainingMaterials.length === 0 &&
              times(6, (index) => <TrainingMaterialListItemSkeleton key={index} />)}

            {trainingMaterials.map((trainingMaterial) => (
              <TrainingMaterialListItem key={trainingMaterial.Id} disabled={loading} item={trainingMaterial}>
                <Button
                  disabled={loading || isAttachInProgress}
                  startIcon={<AddOutlined />}
                  variant='contained'
                  color='primary'
                  size='small'
                  onClick={() =>
                    onAttach({
                      TrainingMaterialId: trainingMaterial.Id,
                      ContentId: contentId!,
                      UserIds: selectedUsers.map((user) => user.id),
                    })
                  }
                >
                  Attach
                </Button>
              </TrainingMaterialListItem>
            ))}
          </List>

          {loaded && trainingMaterials.length === 0 && <NoTrainingMaterialsFoundPlaceholder />}
        </View>

        <View data-active-view={isUsersConfigurationOpen} data-configuration-view>
          <Stack direction='row' paddingInline={2} mb={2} mt={1} gap={1} alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1} flexGrow={1}>
              <Tooltip text='Go back'>
                <IconButton size='small' onClick={() => setIsUsersConfigurationOpen(false)}>
                  <KeyboardArrowLeft />
                </IconButton>
              </Tooltip>
              <Subheader2>{selectedUsers.length} user(s) selected</Subheader2>
            </Stack>
            <Button
              startIcon={allUsersSelected ? <GroupRemoveOutlined /> : <GroupAddOutlined />}
              color={allUsersSelected ? 'error' : 'primary'}
              size='small'
              variant='outlined'
              onClick={allUsersSelected ? onClearAll : onSelectAll}
            >
              {allUsersSelected ? 'Clear all' : 'Select all'}
            </Button>
            <Button
              startIcon={<KeyboardArrowLeft />}
              color='primary'
              size='small'
              variant='outlined'
              onClick={() => setIsUsersConfigurationOpen(false)}
            >
              Go back
            </Button>
          </Stack>
          <Grid container spacing={2} rowSpacing={1} paddingInline={2} overflow='auto'>
            {visibleUsers.map((user) => {
              const selected = selectedUsers.some((selectedUser) => selectedUser.id === user.id)

              return (
                <Grid key={user.id} item xs={6}>
                  <ListItemButton selected={selected} onClick={() => onSelectUser(user)}>
                    <ListItemIcon>
                      <Checkbox edge='start' checked={selected} />
                    </ListItemIcon>
                    <UserInfo
                      item={{
                        Id: user.id,
                        Name: user.displayName,
                        Email: user.email,
                        IsAccountDeleted: user.isAccountDeleted,
                        AvatarUrl: user.avatarUrl,
                      }}
                    />
                  </ListItemButton>
                </Grid>
              )
            })}
          </Grid>
        </View>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(TrainingMaterialsAttachDialog)

